/**
 * @file index.tsx
 *
 * @fileoverview The component displays the first item in the given list.
 * It can display an optional icon. On a user click, it shows popover with
 * the remaining items from the list.
 */
import { ReactNode, useState } from 'react';
import FlexItems from '../../02-atoms/06-layout/03-flex';
import { Popover } from '../../02-atoms/16-popover';

export interface PopoverListProps {
  /**
   * The icon component.
   */
  Icon?: ReactNode;
  /**
   * The gap for the icon and the first item.
   */
  iconGap?: number;
  /**
   * Rendered items.
   */
  items: ReactNode[];
  /**
   * The popover position.
   *
   * @default "bottom"
   */
  position?: 'left' | 'right' | 'top' | 'bottom';
}

export const PopoverList = ({
  Icon,
  iconGap = 4,
  items,
  position = 'bottom',
}: PopoverListProps) => {
  const [shown, setShown] = useState<boolean>(false);

  return items.length > 1 ? (
    <Popover
      position={position}
      onToggle={setShown}
      trigger={
        <FlexItems alignItems="center" gap={iconGap} flexWrap="nowrap">
          {Icon}
          {items[0]} {shown ? '-' : '+'}
        </FlexItems>
      }
    >
      <ul>
        {items.slice(1).map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </Popover>
  ) : Boolean(items[0]) ? (
    <FlexItems alignItems="center" gap={iconGap} flexWrap="nowrap">
      {Icon}
      <span className="popoverlist-no-list-item">{items[0]}</span>
    </FlexItems>
  ) : null;
};

export default PopoverList;
